import { Badge, Card, Icon } from '../../../shared/ui/Primitives';
import type { MonitoringSummary } from '../consumer/contract';
import type { MonitoringReadState } from '../consumer/polling';

export function SyntheticMonitoringNotice() {
  return (
    <div className="nm-notice" role="note">
      <strong>SYNTHETIC · NON-OPERATIONAL · NOT FOR RESCUE USE</strong>
      <p>
        自动监测仅使用本地合成观测。RULE_BASELINE-v1 是规则与基线计算；热成像评分为
        SIMULATED_MODEL_OUTPUT。
      </p>
      <p>结果不构成行动授权；无活动预警、INACTIVE 或已读均不代表区域安全。</p>
    </div>
  );
}
export function MonitoringReadNotice<T>({
  state,
  retry,
  label,
}: {
  readonly state: MonitoringReadState<T>;
  readonly retry: () => void;
  readonly label: string;
}) {
  if (state.status === 'LOADING')
    return (
      <p role="status" aria-busy="true">
        {label}：正在读取本地后端…
      </p>
    );
  if (state.status === 'ERROR')
    return (
      <div className="nm-error" role="status">
        <strong>{label}：局部读取失败</strong>
        <p>
          {state.error}
          {state.data ? ' 保留最后有效内容，数据可能已延迟。' : ' 尚无有效内容；不能显示为零。'}
        </p>
        <button type="button" onClick={retry}>
          重试{label}
        </button>
      </div>
    );
  return null;
}
export function MonitoringSummaryCard({
  state,
  retry,
  overview = false,
}: {
  readonly state: MonitoringReadState<MonitoringSummary>;
  readonly retry: () => void;
  readonly overview?: boolean;
}) {
  const data = state.data;
  const stale = data && Date.now() - Date.parse(data.generatedAt) > 5000;
  return (
    <Card
      zone="nm-summary"
      title={overview ? '自动监测 · 实际后端摘要' : '自动监测摘要'}
      extra={
        <a href="/normal-operations/risk-warning">
          风险预警 <Icon name="arrow" />
        </a>
      }
    >
      <div data-nm-summary>
        <SyntheticMonitoringNotice />
        <MonitoringReadNotice state={state} retry={retry} label="监测摘要" />
        {data && (
          <>
            <p className="nm-scope">
              范围：{data.instanceId} · {data.profileVersion} · {data.algorithmVersion} ·{' '}
              {data.sourceMode}
            </p>
            <p className="p01-muted">
              后端生成：<time>{data.generatedAt}</time> · 最近读取：
              {state.lastReadAt === null ? '未知' : new Date(state.lastReadAt).toISOString()}
            </p>
            {stale && (
              <p className="nm-stale" role="status">
                摘要已延迟：生成时间距当前超过 5 秒；来源健康以最后读取字段为准。
              </p>
            )}
            <div className="nm-metrics">
              <div>
                <span>活动内部预警</span>
                <strong data-nm-active-count>{data.activeWarningCount}</strong>
              </div>
              <div>
                <span>累计预警</span>
                <strong>{data.totalWarningCount}</strong>
              </div>
              <div>
                <span>已接收 / 已处理</span>
                <strong>
                  {data.acceptedCount} / {data.processedCount}
                </strong>
              </div>
              <div>
                <span>待处理 / 拒绝 / 重复</span>
                <strong>
                  {data.pendingCount} / {data.rejectedCount} / {data.duplicateCount}
                </strong>
              </div>
            </div>
            {data.activeWarningCount === 0 && (
              <p className="nm-empty">
                当前无活动预警。该计数不表示所有来源均可用，也不表示区域安全。
              </p>
            )}
            {data.sources.length === 0 ? (
              <p className="nm-empty">后端未返回配置来源；来源覆盖未知。</p>
            ) : (
              data.sources.every((source) => source.health === 'NO_DATA') && (
                <p className="nm-empty">尚无观测数据（NO_DATA）；不能据此认定无风险。</p>
              )
            )}
            <div className="nm-area-grid">
              {data.areas.map((area) => (
                <article key={area.areaId} className="nm-area" data-nm-area={area.areaId}>
                  <h3>
                    {area.areaId} <Badge>{area.availability}</Badge>
                  </h3>
                  <p>
                    来源 {area.sourceCount} · RECENT {area.recentSources} · NO_DATA{' '}
                    {area.noDataSources} · STALE {area.staleSources} · DISABLED{' '}
                    {area.disabledSources}
                  </p>
                  <p>活动预警 {area.activeWarningCount} · 可用性是数据覆盖状态。</p>
                </article>
              ))}
            </div>
            {!overview && (
              <div className="nm-table-wrap">
                <table className="nm-table">
                  <caption>来源健康 · 由后端计算</caption>
                  <thead>
                    <tr>
                      <th>来源 / 区域</th>
                      <th>类别 / 指标</th>
                      <th>健康 / 质量</th>
                      <th>最新观测时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sources.map((source) => (
                      <tr key={source.sourceId} data-nm-source={source.sourceId}>
                        <td>
                          {source.sourceId}
                          <small>{source.areaId}</small>
                        </td>
                        <td>
                          {source.category}
                          <small>
                            {source.metric} · {source.unit}
                          </small>
                        </td>
                        <td>
                          <Badge tone={source.health === 'STALE' ? 'orange' : 'blue'}>
                            {source.health}
                          </Badge>
                          <small>
                            质量 {source.quality ?? '未知'} · {source.enabled ? '启用' : '禁用'}
                          </small>
                        </td>
                        <td>
                          {source.latestObservedAt ?? '无观测（NO_DATA）'}
                          <small>接收：{source.latestReceivedAt ?? '无接收时间'}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {data.limitations.length > 0 && (
              <p className="p01-muted">后端局限：{data.limitations.join('；')}</p>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
